import React, { FC } from "react";
import { Field } from "react-final-form";
import Divider from "@material-ui/core/Divider";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import WeduCheckbox from "../../../../../components/WeduCheckbox/WeduCheckbox";
import BootstrapInput from "../../../../../components/BootstrapInput";
import Autocomplete from "../../../../../components/Autocomplete";
import InfoIcon from "../../../../../components/InfoIcon";
import ShowCurrency from "./ShowCurrency";
import { COMMISSION_TYPES } from "../../../../../constants";
import FieldsGroupLabel from "../common/FieldsGroupLabel";
import { ICourseComponentProps } from "../../../types";
import { calcFeeWithDiscount } from "../../../helpers/helperFunctions";

const getOtherFeesTotal = (activeTabData) => {
  const total = [
    ...(activeTabData?.mainOtherFees || []),
    ...(activeTabData?.addedOtherFees || []),
  ].reduce((sum, fee) => sum + (+fee?.amount || 0), 0);

  return total < 0 ? 0 : total;
};

const useStyles = makeStyles(() => ({
  card: {
    backgroundColor: "#f2f3f4",
    borderRadius: 12,
    padding: "12px 12px 12px 12px",
    "& + &": {
      marginTop: 16,
    },
  },
  checkboxContainer: {
    display: "flex",
  },
  checkboxTitle: {
    fontSize: 14,
    color: "#a1a4ac",
  },
  checkboxLabel: {
    fontSize: 16,
    color: "#000",
    marginLeft: -3,
    marginTop: 2,
  },
  dropDown: {
    "& .MuiInputBase-root": {
      backgroundColor: "#FFF",
    },
  },
  input: {
    "& input": {
      backgroundColor: "#FFF",
    },
  },
  infoIconCont: {
    lineHeight: 2,
    marginLeft: -14,
  },
  tuitionLabel: {
    fontSize: 18,
    color: "#383d4a",
    fontWeight: "bold",
    marginBottom: 12,
  },
  price: {
    fontSize: 18,
    color: "#61657e",
    fontWeight: "bold",
    marginLeft: 18,
  },
  divider: {
    color: "#dae1ed",
    marginTop: 16,
    marginBottom: 8,
  },
  boldStyle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#293858",
  },
  oldTuition: {
    marginLeft: 50,
    textDecoration: "line-through",
  },
  oldOtherFees: {
    marginLeft: 16,
    textDecoration: "line-through",
  },
  calcTuition: {
    marginLeft: 24,
  },
}));

interface ICalculationProps extends ICourseComponentProps {
  currency: string;
  values: any;
}

const Calculation: FC<ICalculationProps> = ({
  fieldsGroupName,
  currency,
  values,
}) => {
  const classes = useStyles();

  const activeTabData = values?.step3;
  const price = +activeTabData?.price;
  const currencyId = activeTabData?.currency?.id;
  const hasPriceDiscount = !!activeTabData?.hasPriceDiscount;
  const hasOtherFeesDiscount = !!activeTabData?.hasOtherFeesDiscount;
  const otherFeesSum = getOtherFeesTotal(activeTabData);

  const {
    feeWithCurrency: tuitionWithCurrency,
    calcFeeWithCurrency: calcTuitionWithCurrency,
    hasSupport: hasTuitionSupport,
  } = calcFeeWithDiscount({
    fee: price,
    hasFeeDiscount: hasPriceDiscount,
    feeDiscountType: activeTabData?.priceDiscountType?.id,
    feeDiscountAmount: activeTabData?.priceDiscountAmount,
    currency: currencyId,
  });

  const {
    feeWithCurrency: otherFeesWithCurrency,
    calcFeeWithCurrency: calcOtherFeesWithCurrency,
    hasSupport: hasOtherFeesSupport,
  } = calcFeeWithDiscount({
    fee: otherFeesSum,
    hasFeeDiscount: hasOtherFeesDiscount,
    feeDiscountType: activeTabData?.otherFeesDiscountType?.id,
    feeDiscountAmount: activeTabData?.otherFeesDiscountAmount,
    currency: currencyId,
  });

  return (
    <div>
      <FieldsGroupLabel label="Calculation" className="fields-group-label" />
      <div className={classes.card}>
        <Typography className={classes.tuitionLabel}>
          Price:
          <Typography component="span" className={classes.price}>
            {tuitionWithCurrency}
          </Typography>
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={3}>
            <Typography className={classes.checkboxTitle}>Discount</Typography>
            <div className={classes.checkboxContainer}>
              <Field
                type="checkbox"
                label={
                  <Typography className={classes.checkboxLabel}>
                    Available
                  </Typography>
                }
                name={`${fieldsGroupName}.hasPriceDiscount`}
                component={WeduCheckbox}
              />
              <div className={classes.infoIconCont}>
                <InfoIcon text="You need to indicate discount is given from total amount or just price" />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.input}>
            <Field
              fullWidth
              type="text"
              label="Amount"
              placeholder="Amount"
              name={`${fieldsGroupName}.priceDiscountAmount`}
              component={BootstrapInput}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.dropDown}>
            <Field
              fullWidth
              label="Type"
              options={COMMISSION_TYPES}
              placeholder="Please Select"
              name={`${fieldsGroupName}.priceDiscountType`}
              component={Autocomplete}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ShowCurrency currency={currency} />
          </Grid>
        </Grid>
      </div>
      <div className={classes.card}>
        <Typography className={classes.tuitionLabel}>
          Other Fees:
          <Typography component="span" className={classes.price}>
            {otherFeesWithCurrency}
          </Typography>
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={3}>
            <Typography className={classes.checkboxTitle}>Discount</Typography>
            <div className={classes.checkboxContainer}>
              <Field
                type="checkbox"
                label={
                  <Typography className={classes.checkboxLabel}>
                    Available
                  </Typography>
                }
                name={`${fieldsGroupName}.hasOtherFeesDiscount`}
                component={WeduCheckbox}
              />
              <div className={classes.infoIconCont}>
                <InfoIcon text="You need to indicate discount is given from total amount or just price" />
              </div>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.input}>
            <Field
              fullWidth
              type="text"
              label="Amount"
              placeholder="Amount"
              name={`${fieldsGroupName}.otherFeesDiscountAmount`}
              component={BootstrapInput}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3} className={classes.dropDown}>
            <Field
              fullWidth
              label="Type"
              options={COMMISSION_TYPES}
              placeholder="Please Select"
              name={`${fieldsGroupName}.otherFeesDiscountType`}
              component={Autocomplete}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <ShowCurrency currency={currency} />
          </Grid>
        </Grid>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.boldStyle}>
        <span>Price:</span>
        {hasTuitionSupport && (
          <span className={classes.oldTuition}>{tuitionWithCurrency}</span>
        )}
        <span className={classes.calcTuition}>{calcTuitionWithCurrency}</span>
      </div>
      <div className={classes.boldStyle}>
        <span>Other Fees:</span>
        {hasOtherFeesSupport && (
          <span className={classes.oldOtherFees}>{otherFeesWithCurrency}</span>
        )}
        <span className={classes.calcTuition}>{calcOtherFeesWithCurrency}</span>
      </div>
    </div>
  );
};

export default Calculation;
